import { useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type { GraffitiStroke } from "./api";
import { aiImage } from "./api";
import { GRAFFITI_W, GRAFFITI_H } from "./GraffitiView";

const SWATCHES = [
  "#111111",
  "#ffffff",
  "#e24b4a",
  "#ef9f27",
  "#f5d000",
  "#1d9e75",
  "#378add",
  "#7f77dd",
  "#d4537e",
];
const BG = "#ffffff";

interface Props {
  onSave: (dataUrl: string, action: "post" | "gift" | "friend") => void;
  onCancel: () => void;
}

interface Cursor {
  x: number;
  y: number;
  size: number;
  show: boolean;
}

export function Graffiti({ onSave, onCancel }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<GraffitiStroke[]>([]);
  const redoRef = useRef<GraffitiStroke[]>([]);
  const currentRef = useRef<GraffitiStroke | null>(null);
  const drawingRef = useRef(false);
  const bgImageRef = useRef<HTMLImageElement | null>(null);

  const [tool, setTool] = useState<"brush" | "eraser">("brush");
  const [color, setColor] = useState("#111111");
  const [size, setSize] = useState(6);
  const [opacity, setOpacity] = useState(1);
  const [recent, setRecent] = useState<string[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [cursor, setCursor] = useState<Cursor>({ x: 0, y: 0, size: 0, show: false });
  const [aiPrompt, setAiPrompt] = useState("");
  const [lastPrompt, setLastPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiStyle, setAiStyle] = useState<"hand" | "art" | "crayon">("hand");

  function applyStyle(g: CanvasRenderingContext2D, s: GraffitiStroke) {
    g.globalAlpha = s.opacity ?? 1;
    g.strokeStyle = s.color;
    g.lineWidth = s.width;
    g.lineCap = "round";
    g.lineJoin = "round";
  }

  function redraw() {
    const canvas = canvasRef.current;
    const g = canvas?.getContext("2d");
    if (!canvas || !g) return;
    g.clearRect(0, 0, canvas.width, canvas.height);
    if (bgImageRef.current) {
      g.drawImage(bgImageRef.current, 0, 0, canvas.width, canvas.height);
    }
    for (const s of strokesRef.current) {
      if (s.points.length === 0) continue;
      g.save();
      applyStyle(g, s);
      g.beginPath();
      const first = s.points[0]!;
      g.moveTo(first[0], first[1]);
      for (let i = 1; i < s.points.length; i++) {
        const p = s.points[i]!;
        g.lineTo(p[0], p[1]);
      }
      g.stroke();
      g.restore();
    }
  }

  function pointAt(e: ReactPointerEvent<HTMLCanvasElement>): [number, number] {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return [
      Math.round((e.clientX - rect.left) * (canvas.width / rect.width)),
      Math.round((e.clientY - rect.top) * (canvas.height / rect.height)),
    ];
  }

  function updateCursor(e: ReactPointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    setCursor({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      size: size * (rect.width / canvas.width),
      show: true,
    });
  }

  function start(e: ReactPointerEvent<HTMLCanvasElement>) {
    drawingRef.current = true;
    redoRef.current = [];
    setCanRedo(false);
    canvasRef.current?.setPointerCapture(e.pointerId);
    updateCursor(e);
    currentRef.current = {
      color: tool === "eraser" ? BG : color,
      width: size,
      opacity: tool === "eraser" ? 1 : opacity,
      points: [pointAt(e)],
    };
  }

  function move(e: ReactPointerEvent<HTMLCanvasElement>) {
    updateCursor(e);
    const s = currentRef.current;
    if (!drawingRef.current || !s) return;
    const point = pointAt(e);
    const prev = s.points[s.points.length - 1];
    s.points.push(point);
    const g = canvasRef.current?.getContext("2d");
    if (g && prev) {
      g.save();
      applyStyle(g, s);
      g.beginPath();
      g.moveTo(prev[0], prev[1]);
      g.lineTo(point[0], point[1]);
      g.stroke();
      g.restore();
    }
  }

  function end() {
    const s = currentRef.current;
    if (s && s.points.length > 0) {
      strokesRef.current.push(s);
      if (tool === "brush") pushRecent(color);
      setCanUndo(true);
    }
    currentRef.current = null;
    drawingRef.current = false;
    setCursor((c) => ({ ...c, show: false }));
  }

  function pushRecent(c: string) {
    setRecent((r) => [c, ...r.filter((x) => x !== c)].slice(0, 6));
  }

  function undo() {
    const s = strokesRef.current.pop();
    if (s) {
      redoRef.current.push(s);
      redraw();
      setCanRedo(true);
    }
    setCanUndo(strokesRef.current.length > 0);
  }

  function redo() {
    const s = redoRef.current.pop();
    if (s) {
      strokesRef.current.push(s);
      redraw();
      setCanUndo(true);
    }
    setCanRedo(redoRef.current.length > 0);
  }

  function clearAll() {
    strokesRef.current = [];
    redoRef.current = [];
    bgImageRef.current = null;
    setCanUndo(false);
    setCanRedo(false);
    redraw();
  }

  async function generateAi(override?: string) {
    const p = (override ?? aiPrompt).trim();
    if (!p || aiBusy) return;
    setAiBusy(true);
    setAiError(null);
    try {
      const { dataUrl } = await aiImage(p, aiStyle);
      const img = new Image();
      img.onload = () => {
        bgImageRef.current = img;
        redraw();
      };
      img.src = dataUrl;
      setLastPrompt(p);
      if (!override) setAiPrompt("");
    } catch {
      setAiError("ИИ не смог нарисовать — попробуй другое описание");
    } finally {
      setAiBusy(false);
    }
  }

  function exportImage(): string {
    const canvas = canvasRef.current;
    if (!canvas) return "";
    // Flatten onto white and export JPEG. Telegram's gift photo
    // (InlineQueryResultPhoto) needs an opaque JPEG, and the canvas can hold
    // transparent pixels (the white is only CSS) — both break inline rendering.
    const flat = document.createElement("canvas");
    flat.width = canvas.width;
    flat.height = canvas.height;
    const fg = flat.getContext("2d");
    if (!fg) return canvas.toDataURL("image/jpeg", 0.92);
    fg.fillStyle = "#ffffff";
    fg.fillRect(0, 0, flat.width, flat.height);
    fg.drawImage(canvas, 0, 0);
    return flat.toDataURL("image/jpeg", 0.92);
  }

  return (
    <div className="graffiti">
      <div className="ai-row">
        <span className="ai-spark">✨</span>
        <input
          className="composer-input"
          placeholder="ИИ нарисует: кот, сердце, молния…"
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void generateAi();
          }}
          disabled={aiBusy}
        />
        <button
          className="send-btn"
          onClick={() => void generateAi()}
          disabled={aiBusy}
          aria-label="Сгенерировать"
        >
          {aiBusy ? "…" : "→"}
        </button>
        {lastPrompt && (
          <button
            className="send-btn"
            onClick={() => void generateAi(lastPrompt)}
            disabled={aiBusy}
            aria-label="Ещё вариант"
            title="Ещё вариант"
          >
            🎲
          </button>
        )}
      </div>
      {aiBusy && <div className="ai-hint">✨ ИИ рисует…</div>}
      {aiError && <div className="ai-hint error">{aiError}</div>}
      <div className="style-row">
        {(
          [
            ["hand", "✏️ От руки"],
            ["art", "🎨 Арт"],
            ["crayon", "🖍 Мелки"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            className={`style-chip${aiStyle === k ? " active" : ""}`}
            onClick={() => setAiStyle(k)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="tool-row">
        <button
          className={`tool${tool === "brush" ? " active" : ""}`}
          onClick={() => setTool("brush")}
        >
          🖌 Кисть
        </button>
        <button
          className={`tool${tool === "eraser" ? " active" : ""}`}
          onClick={() => setTool("eraser")}
        >
          🧽 Ластик
        </button>
        <span className="spacer" />
        <button className="tool icon" onClick={undo} disabled={!canUndo} aria-label="Отменить">
          ↶
        </button>
        <button className="tool icon" onClick={redo} disabled={!canRedo} aria-label="Вернуть">
          ↷
        </button>
        <button className="tool icon" onClick={clearAll} aria-label="Очистить">
          🗑
        </button>
      </div>

      <div className="color-row">
        <label className="color-pick" style={{ background: color }}>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
        </label>
        {SWATCHES.map((c) => (
          <button
            key={c}
            className={`swatch${c === color ? " active" : ""}`}
            style={{ background: c }}
            onClick={() => {
              setColor(c);
              setTool("brush");
            }}
            aria-label={`Цвет ${c}`}
          />
        ))}
        {recent.map((c) => (
          <button
            key={`r-${c}`}
            className="swatch small"
            style={{ background: c }}
            onClick={() => setColor(c)}
            aria-label={`Недавний ${c}`}
          />
        ))}
      </div>

      <div className="slider-row">
        <span className="slider-label">Размер</span>
        <input
          type="range"
          min={2}
          max={40}
          value={size}
          onChange={(e) => setSize(Number(e.target.value))}
        />
        <span className="dot" style={{ width: size, height: size, background: color }} />
      </div>

      <div className="slider-row">
        <span className="slider-label">Прозрачность</span>
        <input
          type="range"
          min={20}
          max={100}
          value={Math.round(opacity * 100)}
          onChange={(e) => setOpacity(Number(e.target.value) / 100)}
        />
        <span className="slider-val">{Math.round(opacity * 100)}%</span>
      </div>

      <div className="canvas-wrap">
        <canvas
          ref={canvasRef}
          width={GRAFFITI_W}
          height={GRAFFITI_H}
          className="canvas"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        />
        {cursor.show && (
          <span
            className="brush-cursor"
            style={{
              left: cursor.x,
              top: cursor.y,
              width: Math.max(cursor.size, 6),
              height: Math.max(cursor.size, 6),
              borderColor: tool === "eraser" ? "var(--hint)" : color,
            }}
          />
        )}
      </div>

      <div className="graffiti-actions">
        <button className="ghost" onClick={onCancel}>
          Отмена
        </button>
        <button className="send-btn wide" onClick={() => onSave(exportImage(), "post")}>
          Отправить 🎨
        </button>
        <button className="send-btn wide gift" onClick={() => onSave(exportImage(), "gift")}>
          🎁 Подарить
        </button>
        <button className="send-btn wide friend" onClick={() => onSave(exportImage(), "friend")}>
          📤 Другу
        </button>
      </div>
    </div>
  );
}
